import React, { useEffect, useState } from "react";
import { Eye, Loader2, Users } from "lucide-react";
import { Button } from "../ui/button";
import { useAuth } from "../../lib/auth-context";
import { roleHome } from "../../lib/role-home";

import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

/** Strip height: one row on phones, so the app header always clears it. */
const BANNER_HEIGHT = "2.5rem";

/** Distinct, white-text-safe pill colours, one per child. */
const CHILD_COLORS = [
  "#2563eb", // blue
  "#db2777", // pink
  "#059669", // emerald
  "#d97706", // amber
  "#7c3aed", // violet
  "#dc2626", // red
  "#0891b2", // cyan
  "#65a30d", // lime
];

/**
 * Colour by the child's rank among siblings sorted by id: siblings never share a colour
 * (up to the palette size) and each keeps theirs whichever child is selected.
 */
function colorFor(id: string | number, siblingIds: (string | number)[]): string {
  const rank = [...siblingIds].map(Number).sort((a, b) => a - b).indexOf(Number(id));
  return CHILD_COLORS[Math.max(rank, 0) % CHILD_COLORS.length];
}

/**
 * One row on every screen: the name truncates, the wording shortens on phones
 * and the button keeps its verb only. Nothing wraps.
 */
const ROW =
  "sticky top-0 z-40 flex items-center gap-2 px-3 text-sm font-medium sm:justify-center sm:gap-3 sm:px-4";

/**
 * Shown while someone browses the app as another user. A super admin only looks
 * (read-only, one click to leave); a parent acting as their child has full
 * rights and a shortcut back to the profile picker.
 */
export function ImpersonationBanner() {
  const { t } = useTranslation("common");
  const { user, stopImpersonation, startImpersonation } = useAuth();
  const navigate = useNavigate();
  // Child being switched to: a veil with a spinner covers the screen under the strip until the new page is in.
  const [switching, setSwitching] = useState(false);
  const showing = !!user?.impersonator;

  // The app header and sticky page bars sit below the strip: they read its height from --banner-h.
  useEffect(() => {
    if (!showing) return;
    const root = document.documentElement;
    root.style.setProperty("--banner-h", BANNER_HEIGHT);
    return () => {
      root.style.removeProperty("--banner-h");
    };
  }, [showing]);

  if (!showing || !user) return null;

  if (user.actingAs === "guardian") {
    const children = user.children ?? [];
    return (
      <>
      <div className={`${ROW} bg-primary text-primary-foreground`} style={{ height: BANNER_HEIGHT }}>
        <Users className="h-4 w-4 shrink-0" aria-hidden />
        <div className="flex min-w-0 items-center gap-1.5" role="group" aria-label={t("guardianBanner.acting", { name: user.name })}>
          {(children.some((c) => String(c.id) === String(user.id)) ? children : [{ id: user.id, name: user.name, firstName: user.firstName, club: null }]).map((child, _, list) => {
            const selected = String(child.id) === String(user.id);
            const hex = colorFor(child.id, list.map((c) => c.id));
            return selected ? (
              // The child in use: a white chip in their colour, so it reads as "this one" on any banner theme.
              <span
                key={child.id}
                aria-current="true"
                title={child.name}
                style={{ color: hex }}
                className="flex h-7 min-w-0 items-center rounded-full bg-white px-3 text-sm font-bold shadow-md"
              >
                <span className="truncate">{child.firstName}</span>
              </span>
            ) : (
              <button
                key={child.id}
                type="button"
                title={child.name}
                style={{ backgroundColor: hex }}
                className={`shrink-0 rounded-full px-2 py-0.5 text-xs font-semibold text-white opacity-75 transition hover:opacity-100 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary-foreground`}
                disabled={switching}
                onClick={async () => {
                  setSwitching(true);
                  try {
                    // A short floor, so even a fast switch reads as a change of person.
                    const [target] = await Promise.all([
                      startImpersonation(`#${child.id}`, child.club?.id ?? null),
                      new Promise((resolve) => setTimeout(resolve, 350)),
                    ]);
                    navigate(target ? roleHome(target) ?? "/" : "/");
                  } finally {
                    setSwitching(false);
                  }
                }}
              >
                {child.firstName}
              </button>
            );
          })}
        </div>
      </div>
      {switching && (
        <div
          role="status"
          aria-label={t("guardianBanner.switching")}
          className="fixed inset-x-0 bottom-0 z-[35] flex items-center justify-center bg-background/70 backdrop-blur-sm animate-in fade-in-0"
          style={{ top: BANNER_HEIGHT }}
        >
          <Loader2 className="h-8 w-8 animate-spin text-primary" aria-hidden />
        </div>
      )}
      </>
    );
  }

  const leave = async () => {
    await stopImpersonation();
    window.location.href = "/";
  };

  return (
    <div className={`${ROW} bg-warning text-warning-foreground`} style={{ height: BANNER_HEIGHT }}>
      <Eye className="h-4 w-4 shrink-0" aria-hidden />
      <span className="min-w-0 truncate">
        <span className="sm:hidden">{t("impersonation.short", { name: user.name })}</span>
        <span className="hidden sm:inline">
          {t("impersonation.banner", { name: user.name, email: user.email ?? t("impersonation.noEmail") })}
        </span>
      </span>
      <Button size="sm" variant="secondary" className="ml-auto h-7 shrink-0 px-2.5 sm:ml-0" onClick={leave}>
        {t("impersonationBanner.quitter")}
      </Button>
    </div>
  );
}
